/**
 * Adjustment combination utilities
 *
 * Handles combining baseline (from Sati) and user adjustments
 * for the "Complete look" profile save option.
 */

// Default color grading values
const DEFAULT_COLOR_GRADING = {
  lift: { r: 0, g: 0, b: 0 },
  gamma: { r: 1, g: 1, b: 1 },
  gain: { r: 1, g: 1, b: 1 },
};

/**
 * Clamp a value between min and max
 */
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * Combine color grading values
 *
 * - Lift (shadows): additive, clamped to [-1, 1]
 * - Gamma (midtones): multiplicative, clamped to [0.5, 2]
 * - Gain (highlights): multiplicative, clamped to [0, 2]
 */
function combineColorGrade(base, user) {
  if (!base && !user) return DEFAULT_COLOR_GRADING;
  if (!base) return user;
  if (!user) return base;

  return {
    lift: {
      r: clamp(base.lift.r + user.lift.r, -1, 1),
      g: clamp(base.lift.g + user.lift.g, -1, 1),
      b: clamp(base.lift.b + user.lift.b, -1, 1),
    },
    gamma: {
      r: clamp(base.gamma.r * user.gamma.r, 0.5, 2),
      g: clamp(base.gamma.g * user.gamma.g, 0.5, 2),
      b: clamp(base.gamma.b * user.gamma.b, 0.5, 2),
    },
    gain: {
      r: clamp(base.gain.r * user.gain.r, 0, 2),
      g: clamp(base.gain.g * user.gain.g, 0, 2),
      b: clamp(base.gain.b * user.gain.b, 0, 2),
    },
  };
}

/**
 * Combine baseline and user adjustments to create a "complete look" profile.
 *
 * Different adjustment types use different combination methods:
 * - Additive: exposure, temperature, tint (add values, clamp to range)
 * - Override: contrast, highlights, shadows, etc. (user value wins if non-zero)
 * - Replace: sharpening, denoise (user value takes precedence if set)
 * - Special: color_grading (lift additive, gamma/gain multiplicative)
 *
 * @param {Object} baseline - Adjustments from Sati/profile (the base)
 * @param {Object} user - User's incremental adjustments
 * @returns {Object} Combined adjustments
 */
export function combineAdjustments(baseline, user) {
  if (!baseline) return user || {};
  if (!user) return baseline;

  // Get values with defaults
  const b = baseline;
  const u = user;

  return {
    // Additive adjustments - add values and clamp
    exposure: clamp((b.exposure || 0) + (u.exposure || 0), -3, 3),
    temperature: clamp((b.temperature || 0) + (u.temperature || 0), -100, 100),
    tint: clamp((b.tint || 0) + (u.tint || 0), -100, 100),

    // Override adjustments - user intent wins if non-zero
    contrast: (u.contrast || 0) !== 0 ? u.contrast : (b.contrast || 0),
    highlights: (u.highlights || 0) !== 0 ? u.highlights : (b.highlights || 0),
    shadows: (u.shadows || 0) !== 0 ? u.shadows : (b.shadows || 0),
    whites: (u.whites || 0) !== 0 ? u.whites : (b.whites || 0),
    blacks: (u.blacks || 0) !== 0 ? u.blacks : (b.blacks || 0),
    clarity: (u.clarity || 0) !== 0 ? u.clarity : (b.clarity || 0),

    // Replace adjustments - user takes precedence if set
    sharpening: u.sharpening?.amount > 0 ? u.sharpening : b.sharpening,
    denoise: (u.denoise || 0) > 0 ? u.denoise : (b.denoise || 0),

    // Color grading - special handling
    color_grading: combineColorGrade(b.color_grading, u.color_grading),
  };
}

/**
 * Format adjustments into a human-readable summary.
 *
 * @param {Object} adjustments - The adjustments object
 * @returns {string} Summary like "Exposure +0.5, Contrast +15"
 */
export function formatAdjustmentSummary(adjustments) {
  if (!adjustments) return "";

  const parts = [];

  // Basic adjustments
  const basicKeys = [
    { key: "exposure", label: "Exposure", format: (v) => v > 0 ? `+${v}` : v },
    { key: "contrast", label: "Contrast", format: (v) => v > 0 ? `+${v}` : v },
    { key: "highlights", label: "Highlights", format: (v) => v > 0 ? `+${v}` : v },
    { key: "shadows", label: "Shadows", format: (v) => v > 0 ? `+${v}` : v },
    { key: "whites", label: "Whites", format: (v) => v > 0 ? `+${v}` : v },
    { key: "blacks", label: "Blacks", format: (v) => v > 0 ? `+${v}` : v },
    { key: "clarity", label: "Clarity", format: (v) => v > 0 ? `+${v}` : v },
    { key: "temperature", label: "Temp", format: (v) => v > 0 ? `+${v}` : v },
    { key: "tint", label: "Tint", format: (v) => v > 0 ? `+${v}` : v },
  ];

  for (const { key, label, format } of basicKeys) {
    const value = adjustments[key];
    if (value && value !== 0) {
      parts.push(`${label} ${format(value)}`);
    }
  }

  // Sharpening
  if (adjustments.sharpening?.amount > 0) {
    parts.push(`Sharpening ${adjustments.sharpening.amount}`);
  }

  // Denoise
  if (adjustments.denoise > 0) {
    parts.push(`Denoise ${adjustments.denoise}`);
  }

  // Color grading (simplified - just note if present)
  if (adjustments.color_grading) {
    const cg = adjustments.color_grading;
    const hasLift = cg.lift && (cg.lift.r !== 0 || cg.lift.g !== 0 || cg.lift.b !== 0);
    const hasGamma = cg.gamma && (cg.gamma.r !== 1 || cg.gamma.g !== 1 || cg.gamma.b !== 1);
    const hasGain = cg.gain && (cg.gain.r !== 1 || cg.gain.g !== 1 || cg.gain.b !== 1);
    if (hasLift || hasGamma || hasGain) {
      parts.push("Color grading");
    }
  }

  return parts.join(", ");
}

/**
 * Check if adjustments have any non-default values.
 *
 * @param {Object} adjustments - The adjustments object
 * @returns {boolean} True if any adjustment is non-default
 */
export function hasNonDefaultAdjustments(adjustments) {
  if (!adjustments) return false;
  return formatAdjustmentSummary(adjustments).length > 0;
}
